import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Search, CheckCircle, XCircle, Clock, AlertTriangle, Ticket,
  User, Car, Phone, Calendar, Building2, Hash, ShieldCheck, Eye
} from "lucide-react";

const STATUS_META = {
  borrador: { label: "Borrador", color: "bg-slate-100 text-slate-600 border-slate-200" },
  emitido: { label: "Emitido", color: "bg-blue-50 text-blue-700 border-blue-200" },
  enviado: { label: "Enviado", color: "bg-violet-50 text-violet-700 border-violet-200" },
  entregado: { label: "Entregado", color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  canjeado: { label: "Canjeado", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  caducado: { label: "Caducado", color: "bg-amber-50 text-amber-700 border-amber-200" },
  anulado: { label: "Anulado", color: "bg-red-50 text-red-700 border-red-200" },
};

export default function VoucherRedeem() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [redeemDialog, setRedeemDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [redeemNotes, setRedeemNotes] = useState("");
  const [redeemCenter, setRedeemCenter] = useState("");
  const [redeemAdvisor, setRedeemAdvisor] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [centers, setCenters] = useState([]);
  const [sellers, setSellers] = useState([]);

  useEffect(() => {
    axiosAuth().get("/vouchers/config").then(r => {
      setCenters(r.data.centers || []);
      setSellers(r.data.sellers || []);
    }).catch(() => {});
  }, [axiosAuth]);

  const [forbidden, setForbidden] = useState(false);
  const [scope, setScope] = useState(null); // {scope, label}

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true); setSelected(null); setForbidden(false);
    try {
      const r = await axiosAuth().get(`/vouchers/search?q=${encodeURIComponent(query.trim())}`);
      setResults(r.data.results || []);
      if (r.data.scope) setScope({ scope: r.data.scope, label: r.data.scope_label });
      if ((r.data.results || []).length === 0) {
        toast.info("No se encontraron bonos");
      }
    } catch (e) {
      const st = e?.response?.status;
      if (st === 403) {
        setForbidden(true);
        setResults([]);
        toast.error("Sin permisos para buscar bonos");
      } else if (st === 401) {
        toast.error("Sesión expirada. Vuelve a iniciar sesión.");
      } else if (st >= 500) {
        toast.error(e?.response?.data?.detail || "Error del servidor al buscar");
      } else {
        toast.error(e?.response?.data?.detail || "Error en la búsqueda");
      }
    }
    finally { setSearching(false); }
  };

  const handleRedeem = async () => {
    if (!selected) return;
    setRedeeming(true);
    try {
      await axiosAuth().post("/vouchers/redeem", {
        code: selected.code, notes: redeemNotes,
        center: redeemCenter, advisor: redeemAdvisor
      });
      toast.success(`Bono ${selected.code} canjeado correctamente`);
      setRedeemDialog(false); setConfirmDialog(false);
      setRedeemNotes(""); setRedeemCenter(""); setRedeemAdvisor("");
      setSelected(null); setResults([]); setQuery("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al canjear");
    } finally { setRedeeming(false); }
  };

  const openRedeemDialog = () => {
    setRedeemNotes(""); setRedeemCenter(""); setRedeemAdvisor("");
    setRedeemDialog(true); setConfirmDialog(false);
  };

  const canRedeem = selected && ["emitido", "enviado", "entregado"].includes(selected.status);
  const today = new Date().toISOString().slice(0, 10);
  const isExpired = selected?.expiry_date && selected.expiry_date < today;

  return (
    <div className="space-y-6" data-testid="voucher-redeem">
      <div>
        <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase tracking-tight">Canjear Bono</h1>
        <p className="text-sm text-slate-500 mt-1">Busca un bono por codigo, matricula, nombre o telefono</p>
      </div>

      {/* Search */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-5">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={query} onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSearch()}
                placeholder="Codigo del bono, matricula, nombre o telefono..."
                className="pl-10 rounded-sm h-11 border-slate-200 text-base" data-testid="redeem-search-input" />
            </div>
            <Button onClick={handleSearch} disabled={searching}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm h-11 px-6 text-xs uppercase tracking-wider font-semibold" data-testid="search-btn">
              {searching ? "Buscando..." : "Buscar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {scope && (
        <div className={`text-xs px-3 py-2 rounded-sm border flex items-center gap-2 ${
          scope.scope === "todos"
            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : "bg-amber-50 text-amber-800 border-amber-200"
        }`} data-testid="voucher-search-scope-banner">
          <Eye className="w-3.5 h-3.5" />
          <span className="font-semibold">{scope.label}</span>
          {scope.scope !== "todos" && (
            <span className="text-[10px] text-amber-700 ml-auto italic">
              Configurable por admin en Usuarios → tu rol
            </span>
          )}
        </div>
      )}

      {forbidden && (
        <Card className="rounded-sm border-red-200 bg-red-50/30" data-testid="voucher-search-forbidden">
          <CardContent className="p-6 text-center">
            <ShieldCheck className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-sm font-semibold text-red-700">Sin permisos para buscar bonos</p>
            <p className="text-xs text-slate-500 mt-1">
              Pide a un administrador que añada el permiso <code className="bg-slate-100 px-1 rounded">ver</code> sobre el módulo <code className="bg-slate-100 px-1 rounded">atenciones_comerciales</code> a tu rol.
            </p>
          </CardContent>
        </Card>
      )}

      {results.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400">{results.length} resultado{results.length !== 1 ? "s" : ""}</p>
          {results.map(v => {
            const sm = STATUS_META[v.status] || STATUS_META.borrador;
            const isSelected = selected?.id === v.id;
            return (
              <Card key={v.id}
                className={`border rounded-sm cursor-pointer transition-all ${isSelected ? "border-slate-900 bg-slate-50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}
                onClick={() => setSelected(v)} data-testid={`result-${v.code}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-sm font-mono font-bold text-slate-900">{v.code}</span>
                        <Badge variant="outline" className={`h-5 text-[9px] rounded-sm font-semibold border ${sm.color}`}>{sm.label}</Badge>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" /> {v.client_name || "—"}</span>
                        {v.plate && <span className="flex items-center gap-1 font-mono"><Car className="w-3 h-3" /> {v.plate}</span>}
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Caduca: {v.expiry_date}</span>
                      </div>
                    </div>
                    {isSelected && <CheckCircle className="w-5 h-5 text-slate-900" />}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Selected voucher detail */}
      {selected && (
        <Card className={`rounded-sm overflow-hidden ${isExpired ? "border-amber-300" : selected.status === "canjeado" ? "border-emerald-300" : selected.status === "anulado" ? "border-red-300" : "border-slate-200"}`}>
          <div className="h-1" style={{ backgroundColor: selected.template_snapshot?.bar_color || "#0f172a" }} />
          <CardHeader className="pb-2 pt-4">
            <CardTitle className="text-xs uppercase tracking-[0.12em] font-bold text-slate-500 flex items-center gap-2">
              <Hash className="w-4 h-4" /> Detalle del bono seleccionado
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pb-5">
            {/* Status warnings */}
            {selected.status === "canjeado" && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-800">
                  <p className="font-bold">Bono ya canjeado</p>
                  <p className="text-xs mt-0.5">Canjeado el {selected.redeemed_at?.slice(0, 10)} por {selected.redeemed_by} en {selected.redeem_center}</p>
                </div>
              </div>
            )}
            {selected.status === "anulado" && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-4 flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 font-bold">Bono anulado — no puede canjearse</p>
              </div>
            )}
            {isExpired && !["canjeado", "anulado", "caducado"].includes(selected.status) && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 font-bold">Bono caducado — fecha de validez superada ({selected.expiry_date})</p>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
              {[
                { label: "Codigo", value: selected.code, mono: true, bold: true },
                { label: "Cliente", value: selected.client_name, bold: true },
                { label: "Matricula", value: selected.plate, mono: true },
                { label: "Telefono", value: selected.client_phone },
                { label: "Centro emisor", value: selected.center },
                { label: "Emisor", value: selected.issuer_name },
                { label: "Emision", value: selected.issue_date },
                { label: "Caducidad", value: selected.expiry_date, bold: true },
                { label: "Tipo", value: selected.template_snapshot?.name || selected.type },
              ].map((f, i) => (
                <div key={i}>
                  <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-bold mb-0.5">{f.label}</p>
                  <p className={`text-sm ${f.bold ? "font-bold" : "font-medium"} ${f.value ? "text-slate-800" : "text-slate-300"} ${f.mono ? "font-mono" : ""}`}>
                    {f.value || "—"}
                  </p>
                </div>
              ))}
            </div>
            {selected.observations && (
              <div className="pt-2 border-t border-slate-100">
                <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-bold mb-0.5">Observaciones</p>
                <p className="text-xs text-slate-700">{selected.observations}</p>
              </div>
            )}

            <div className="flex gap-2 pt-3 border-t border-slate-200">
              {canRedeem && !isExpired && (
                <Button onClick={openRedeemDialog}
                  className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm text-xs uppercase tracking-wider h-10 px-6 font-semibold" data-testid="redeem-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> Canjear bono
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate(`/admin/atenciones/detalle/${selected.id}`)}
                className="rounded-sm text-xs uppercase tracking-wider h-10 px-4 font-semibold">
                Ver ficha completa
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Redeem dialog with center & advisor dropdowns */}
      <Dialog open={redeemDialog && !confirmDialog} onOpenChange={o => { if (!o) setRedeemDialog(false); }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="redeem-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <CheckCircle className="w-5 h-5 text-emerald-600" /> Canjear bono
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Selecciona el centro y el asesor que realiza el canje.
              {selected?.client_email && " Se enviara un email de confirmacion al cliente."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-center">
              <p className="text-lg font-mono font-bold text-slate-900">{selected?.code}</p>
              <p className="text-xs text-slate-500 mt-0.5">{selected?.client_name} — {selected?.plate}</p>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Centro de canje</Label>
              <Select value={redeemCenter || "none"} onValueChange={v => setRedeemCenter(v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-9 border-slate-200 mt-1" data-testid="redeem-center-select"><SelectValue placeholder="Seleccionar centro" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin especificar</SelectItem>
                  {centers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Asesor que canjea</Label>
              <Select value={redeemAdvisor || "none"} onValueChange={v => setRedeemAdvisor(v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-9 border-slate-200 mt-1" data-testid="redeem-advisor-select"><SelectValue placeholder="Seleccionar asesor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin especificar</SelectItem>
                  {sellers.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Notas de canje</Label>
              <Textarea value={redeemNotes} onChange={e => setRedeemNotes(e.target.value)}
                placeholder="Notas opcionales..." className="rounded-sm border-slate-200 mt-1" rows={2} data-testid="redeem-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRedeemDialog(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={() => setConfirmDialog(true)} disabled={redeeming}
              className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm" data-testid="confirm-redeem-btn">
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Double confirmation dialog */}
      <Dialog open={confirmDialog} onOpenChange={o => { if (!o) setConfirmDialog(false); }}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="redeem-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <AlertTriangle className="w-5 h-5 text-emerald-600" /> Confirmar canje
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-2">
              Vas a canjear el bono <strong className="font-mono">{selected?.code}</strong> de <strong>{selected?.client_name}</strong>.
              {selected?.client_email && ` Se enviara un email de confirmacion a ${selected.client_email}.`}
              {" "}Esta accion no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setConfirmDialog(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={handleRedeem} disabled={redeeming}
              className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm" data-testid="final-redeem-btn">
              {redeeming ? "Canjeando..." : "Si, canjear bono"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
